import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { BellIcon, CheckCircle2Icon, ChevronDownIcon, MailIcon, MessageCircleIcon, PaperclipIcon, PlusIcon, SendHorizontalIcon, SlidersHorizontalIcon, UserRoundIcon, XIcon } from 'lucide-react';
type MessageCategory = 'Action needed' | 'Update' | 'Conversation';
type MessageAuthor = 'ManyCo' | 'Brand';
type InboxSort = 'Recent' | 'Waiting for reply';
type ThreadMessage = {
  id: number;
  author: MessageAuthor;
  authorName: string;
  timestamp: string;
  text: string;
};
type BrandThread = {
  id: string;
  brandId: string;
  title: string;
  category: MessageCategory;
  preview: string;
  updatedAt: string;
  unread: boolean;
  waitingForReply: boolean;
  messages: ThreadMessage[];
};
type BrandAccount = {
  id: string;
  name: string;
  assignedRep: string;
  coverageRep: string;
  coverageNote?: string;
};
type Contact = {
  id: string;
  name: string;
  role: string;
  initials: string;
  available: boolean;
  tone: string;
};
const contacts: Contact[] = [{
  id: 'meris',
  name: 'Meris',
  role: 'Head of Partnerships',
  initials: 'MP',
  available: false,
  tone: 'bg-[#A77ACA]'
}, {
  id: 'meg',
  name: 'Meg',
  role: 'Sales representative',
  initials: 'MG',
  available: true,
  tone: 'bg-[#C97968]'
}, {
  id: 'service',
  name: 'Customer service',
  role: 'Operations support',
  initials: 'CS',
  available: true,
  tone: 'bg-[#4D9CC7]'
}];
const brands: BrandAccount[] = [{
  id: 'eddy',
  name: 'Eddy',
  assignedRep: 'meris',
  coverageRep: 'meg',
  coverageNote: 'Meris is away · Meg is covering'
}, {
  id: 'one-people',
  name: '1 People',
  assignedRep: 'meg',
  coverageRep: 'meg'
}, {
  id: 'gap',
  name: 'GAP',
  assignedRep: 'meris',
  coverageRep: 'meris'
}, {
  id: 'nature-baby',
  name: 'Nature Baby',
  assignedRep: 'meg',
  coverageRep: 'meg'
}];
const initialThreads: BrandThread[] = [{
  id: 'eddy-plan',
  brandId: 'eddy',
  title: 'Approval needed: Summer recovery plan',
  category: 'Action needed',
  preview: 'Your proposed pricing and channel plan is ready to review.',
  updatedAt: '12m',
  unread: true,
  waitingForReply: false,
  messages: [{
    id: 1,
    author: 'ManyCo',
    authorName: 'Meg · ManyCo',
    timestamp: 'Today, 9:42 AM',
    text: 'We completed the summer recovery plan using current inventory, channel rules, and pricing guardrails. Please review the proposed channel mix and approve when ready.'
  }, {
    id: 2,
    author: 'ManyCo',
    authorName: 'Carli',
    timestamp: 'Today, 9:43 AM',
    text: 'The recommendation prioritizes 142 units with the strongest recovery opportunity. No items will be published until your approval is recorded.'
  }]
}, {
  id: 'eddy-question',
  brandId: 'eddy',
  title: 'Question about approved channels',
  category: 'Conversation',
  preview: 'Can we keep the capsule collection off marketplace channels?',
  updatedAt: '43m',
  unread: true,
  waitingForReply: true,
  messages: [{
    id: 3,
    author: 'Brand',
    authorName: 'Eddy team',
    timestamp: 'Today, 9:12 AM',
    text: 'Can we keep the capsule collection off marketplace channels while still including the core assortment?'
  }]
}, {
  id: 'eddy-inventory',
  brandId: 'eddy',
  title: 'Inventory file received',
  category: 'Update',
  preview: 'Your June inventory export has been added to the recovery workspace.',
  updatedAt: 'Yesterday',
  unread: false,
  waitingForReply: false,
  messages: [{
    id: 4,
    author: 'ManyCo',
    authorName: 'ManyCo operations',
    timestamp: 'Yesterday, 3:18 PM',
    text: 'Your June inventory export has been received and is now available in Inventory. Carli will use it in the next recovery review.'
  }]
}, {
  id: 'people-pricing',
  brandId: 'one-people',
  title: 'Request to review floor pricing',
  category: 'Conversation',
  preview: 'Could we revisit the approved floor for outerwear?',
  updatedAt: '1h',
  unread: true,
  waitingForReply: true,
  messages: [{
    id: 5,
    author: 'Brand',
    authorName: '1 People team',
    timestamp: 'Today, 8:36 AM',
    text: 'Could we revisit the approved floor price for the outerwear assortment before the next publishing window?'
  }]
}, {
  id: 'gap-recap',
  brandId: 'gap',
  title: 'May recovery recap',
  category: 'Update',
  preview: 'A summary of recovered value, sell-through, and active channels.',
  updatedAt: 'May 31',
  unread: false,
  waitingForReply: false,
  messages: [{
    id: 6,
    author: 'ManyCo',
    authorName: 'Meris · ManyCo',
    timestamp: 'May 31, 4:00 PM',
    text: 'Your May recovery recap is ready. You can see recovered value, sell-through, and channel activity in Performance and Finances.'
  }]
}, {
  id: 'nature-baby-submission',
  brandId: 'nature-baby',
  title: 'Inventory submission needs confirmation',
  category: 'Action needed',
  preview: 'Please confirm the unit counts on your latest submission.',
  updatedAt: '3h',
  unread: true,
  waitingForReply: true,
  messages: [{
    id: 7,
    author: 'Brand',
    authorName: 'Nature Baby team',
    timestamp: 'Today, 6:58 AM',
    text: 'We sent over our summer submission but the unit counts look different from our export. Can you confirm what was received?'
  }]
}];
const categories: Array<MessageCategory | 'All'> = ['All', 'Action needed', 'Update', 'Conversation'];
function getBrandIdForOwner(owner: string) {
  return brands.find(brand => brand.name.toLocaleLowerCase() === owner.trim().toLocaleLowerCase())?.id ?? null;
}
export function BrandMessagesDashboard({
  selectedOwner
}: {
  selectedOwner: string;
}) {
  const defaultBrandId = getBrandIdForOwner(selectedOwner) ?? 'eddy';
  const [threads, setThreads] = useState(initialThreads);
  const [selectedBrandId, setSelectedBrandId] = useState(defaultBrandId);
  const [activeId, setActiveId] = useState(initialThreads.find(thread => thread.brandId === defaultBrandId)?.id ?? 'eddy-plan');
  useEffect(() => {
    const brandId = getBrandIdForOwner(selectedOwner);
    if (!brandId) return;
    setSelectedBrandId(brandId);
    const firstThread = initialThreads.find(thread => thread.brandId === brandId);
    if (firstThread) setActiveId(firstThread.id);
  }, [selectedOwner]);
  const [category, setCategory] = useState<MessageCategory | 'All'>('All');
  const [sort, setSort] = useState<InboxSort>('Recent');
  const [showBrandMessages, setShowBrandMessages] = useState(true);
  const [brandPickerOpen, setBrandPickerOpen] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [newMessageOpen, setNewMessageOpen] = useState(false);
  const [recipientId, setRecipientId] = useState('meg');
  const [subject, setSubject] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [reply, setReply] = useState('');
  const selectedBrand = brands.find(brand => brand.id === selectedBrandId) ?? brands[0];
  const currentContact = contacts.find(contact => contact.id === selectedBrand.coverageRep) ?? contacts[1];
  const waitingCount = threads.filter(thread => thread.waitingForReply).length;
  const activeThread = threads.find(thread => thread.id === activeId) ?? threads.find(thread => thread.brandId === selectedBrandId) ?? threads[0];
  const inboxThreads = useMemo(() => {
    const byBrand = threads.filter(thread => thread.brandId === selectedBrandId);
    const byVisibility = showBrandMessages ? byBrand : byBrand.filter(thread => thread.messages.every(message => message.author !== 'Brand'));
    const byCategory = category === 'All' ? byVisibility : byVisibility.filter(thread => thread.category === category);
    return [...byCategory].sort((first, second) => sort === 'Waiting for reply' ? Number(second.waitingForReply) - Number(first.waitingForReply) : 0);
  }, [category, selectedBrandId, showBrandMessages, sort, threads]);
  const selectBrand = (brand: BrandAccount) => {
    setSelectedBrandId(brand.id);
    setRecipientId(brand.coverageRep);
    setBrandPickerOpen(false);
    const first = threads.find(thread => thread.brandId === brand.id);
    if (first) setActiveId(first.id);
  };
  const toggleBrandMessages = () => {
    const next = !showBrandMessages;
    setShowBrandMessages(next);
    if (!next && activeThread.messages.some(message => message.author === 'Brand')) {
      const nextThread = threads.find(thread => thread.brandId === selectedBrandId && thread.messages.every(message => message.author !== 'Brand'));
      if (nextThread) setActiveId(nextThread.id);
    }
  };
  const openThread = (id: string) => {
    setActiveId(id);
    setThreads(current => current.map(thread => thread.id === id ? {
      ...thread,
      unread: false
    } : thread));
  };
  const sendReply = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const text = reply.trim();
    if (!text) return;
    const timestamp = Date.now();
    setThreads(current => current.map(thread => thread.id === activeThread.id ? {
      ...thread,
      preview: text,
      updatedAt: 'Just now',
      unread: false,
      waitingForReply: false,
      messages: [...thread.messages, {
        id: timestamp,
        author: 'ManyCo',
        authorName: 'Rich · ManyCo',
        timestamp: 'Just now',
        text
      }]
    } : thread));
    setReply('');
  };
  const startConversation = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const title = subject.trim();
    const text = newMessage.trim();
    if (!title || !text) return;
    const recipient = contacts.find(contact => contact.id === recipientId) ?? currentContact;
    const item: BrandThread = {
      id: `new-${Date.now()}`,
      brandId: selectedBrandId,
      title,
      category: 'Conversation',
      preview: text,
      updatedAt: 'Just now',
      unread: false,
      waitingForReply: false,
      messages: [{
        id: Date.now(),
        author: 'ManyCo',
        authorName: `Rich · ManyCo to ${recipient.name}`,
        timestamp: 'Just now',
        text
      }]
    };
    setThreads(current => [item, ...current]);
    setActiveId(item.id);
    setSubject('');
    setNewMessage('');
    setNewMessageOpen(false);
  };
  return <div className="mx-auto max-w-7xl px-5 py-6 sm:px-8 lg:py-8" aria-label="Brand messages" data-id="element-42" data-mp="r7myz557ja1f">
    <section className="overflow-visible rounded-3xl border border-ink/10 bg-paper shadow-sm" data-id="element-43" data-mp="m6pn1aasjc8d">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-ink/10 px-4 py-3 sm:px-5" data-id="element-44" data-mp="22kzam277kwy5">
        <div className="relative flex min-w-0 items-center gap-3" data-id="element-45" data-mp="1xme6hrou5t4f">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#BB5E40]/10 text-[#BB5E40]" data-id="element-46" data-mp="2ugj8d5jvjtkg"><MessageCircleIcon className="h-4 w-4" data-id="element-47" data-mp="2pi7yiutkxj28" /></span>
          <div data-id="element-48" data-mp="2kqgihe81uvqc"><p className="text-sm font-semibold" data-id="element-49" data-mp="2kqgi3d450tpt">Messages</p><p className="text-xs text-ink/50" data-id="element-50" data-mp="2kp5ebdaozlyp">Admin workspace · {waitingCount} waiting for reply</p></div>
          <button type="button" onClick={() => setBrandPickerOpen(open => !open)} aria-expanded={brandPickerOpen} className="ml-1 inline-flex max-w-[180px] items-center gap-1.5 rounded-lg border border-ink/10 bg-cream/55 px-2.5 py-1.5 text-xs font-semibold text-ink/70 hover:border-[#BB5E40]/30" data-id="element-51" data-mp="2god5um9n9l3h"><span className="truncate" data-id="element-52" data-mp="21snhwikwexw4">{selectedBrand.name}</span><ChevronDownIcon className={`h-3.5 w-3.5 shrink-0 transition-transform ${brandPickerOpen ? 'rotate-180' : ''}`} data-id="element-53" data-mp="1xq0jtzxmwgqh" /></button>
          {brandPickerOpen && <div className="absolute left-0 top-[calc(100%+10px)] z-30 w-72 rounded-xl border border-ink/10 bg-paper p-1.5 shadow-xl" data-id="element-54" data-mp="3w4c0cuqc9il9">{brands.map(brand => {
              const waiting = threads.filter(thread => thread.brandId === brand.id && thread.waitingForReply).length;
              return <button key={brand.id} type="button" onClick={() => selectBrand(brand)} className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm ${brand.id === selectedBrandId ? 'bg-[#BB5E40]/10 text-ink' : 'text-ink/70 hover:bg-cream'}`} data-id="element-55" data-mp="1iv3s3qvzfm2f"><span className="flex h-7 w-7 items-center justify-center rounded-full bg-ink/8 text-[10px] font-bold text-ink/60" data-id="element-56" data-mp="1rn5qljmhqsa2">{brand.name.slice(0, 2).toUpperCase()}</span><span className="flex-1 font-semibold" data-id="element-57" data-mp="zvzk8lg6fycc">{brand.name}</span>{waiting > 0 && <span className="rounded-full bg-[#C9A04E] px-1.5 py-0.5 text-[10px] font-bold text-ink" data-id="element-58" data-mp="13y2xnfmepd09">{waiting} waiting</span>}</button>;
            })}</div>}
        </div>
        <div className="flex items-center gap-2" data-id="element-59" data-mp="18xz5jpy85ol3"><button type="button" onClick={() => setEmailNotifications(enabled => !enabled)} aria-pressed={emailNotifications} className={`inline-flex items-center gap-2 rounded-lg px-2.5 py-2 text-xs font-semibold ${emailNotifications ? 'bg-forest/8 text-forest' : 'text-ink/55 hover:bg-cream'}`} data-id="element-60" data-mp="2fd9rvkc7ifxw"><BellIcon className="h-3.5 w-3.5" data-id="element-61" data-mp="kc689nvglolb" />Email {emailNotifications ? 'on' : 'off'}</button><button type="button" onClick={() => {
            setRecipientId(currentContact.id);
            setNewMessageOpen(true);
          }} className="inline-flex items-center gap-2 rounded-lg bg-[#BB5E40] px-3 py-2 text-xs font-semibold text-cream hover:bg-[#94452E]" data-id="element-62" data-mp="g88d1o998xxj"><PlusIcon className="h-3.5 w-3.5" data-id="element-63" data-mp="1jlmsri3alrj" />Start message</button></div>
      </div>

      <div className="grid min-h-[640px] lg:grid-cols-[320px_minmax(0,1fr)]" data-id="element-64" data-mp="1hiu1zpjyej4">
        <aside className="flex min-h-0 flex-col border-b border-ink/10 bg-cream/50 p-3 lg:border-b-0 lg:border-r" aria-label="Conversation list" data-id="element-65" data-mp="3srhkcisz5fjd">
          <div className="rounded-xl border border-ink/10 bg-paper p-3" data-id="element-66" data-mp="6iyi80n1115q"><div className="flex items-center justify-between gap-3" data-id="element-67" data-mp="6hdky39bv446"><div data-id="element-68" data-mp="3exqb7bj5nxo6"><p className="text-xs font-semibold" data-id="element-69" data-mp="3exgk1kmdjkgw">Brand-originated messages</p><p className="mt-0.5 text-[11px] text-ink/50" data-id="element-70" data-mp="39zf1r1wrvp6h">Show messages brands sent to ManyCo.</p></div><Toggle checked={showBrandMessages} onChange={toggleBrandMessages} label="Toggle messages from brands" data-id="element-71" data-mp="39xkom9rqt1cy" /></div></div>
          <div className="mt-3 flex items-center justify-between gap-2" data-id="element-72" data-mp="1u8mn27ciehui"><div className="flex gap-1" aria-label="Conversation categories" data-id="element-73" data-mp="1ua5ynkn804py">{categories.map(item => <button key={item} type="button" onClick={() => setCategory(item)} className={`rounded-md px-2 py-1.5 text-[10px] font-semibold ${category === item ? 'bg-ink text-cream' : 'text-ink/55 hover:bg-paper'}`} data-id="element-74" data-mp="1geligwyvm0m8">{item === 'Action needed' ? 'Action' : item}</button>)}</div><button type="button" onClick={() => setSort(current => current === 'Recent' ? 'Waiting for reply' : 'Recent')} className={`inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1.5 text-[10px] font-semibold ${sort === 'Waiting for reply' ? 'bg-[#C9A04E]/15 text-[#8B6A20]' : 'text-ink/55 hover:bg-paper'}`} data-id="element-75" data-mp="2d5w6vplqxxca"><SlidersHorizontalIcon className="h-3 w-3" data-id="element-76" data-mp="nqfwy80007lc" />{sort === 'Recent' ? 'Recent' : 'Waiting'}</button></div>
          <div className="mt-3 min-h-0 flex-1 space-y-1 overflow-y-auto" data-id="element-77" data-mp="ssf1hl7h5ajv">{inboxThreads.map(thread => <button key={thread.id} type="button" onClick={() => openThread(thread.id)} className={`w-full rounded-xl p-3 text-left ${thread.id === activeThread.id ? 'bg-[#BB5E40]/10' : 'hover:bg-paper'}`} data-id="element-78" data-mp="182bowyuaa6d5"><div className="flex gap-2" data-id="element-79" data-mp="ybh2nuh1f6rp"><span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${thread.waitingForReply ? 'bg-[#C9A04E]' : thread.category === 'Update' ? 'bg-forest' : 'bg-[#BB5E40]'}`} data-id="element-80" data-mp="y8usro6ocf6b" /><div className="min-w-0 flex-1" data-id="element-81" data-mp="1qybn4rduu656"><div className="flex items-start justify-between gap-2" data-id="element-82" data-mp="1qxijhza6zwr9"><p className="line-clamp-2 text-xs font-semibold leading-snug text-ink" data-id="element-83" data-mp="1m1l7g55ypcka">{thread.title}</p><span className="shrink-0 text-[10px] text-ink/40" data-id="element-84" data-mp="1hy59n5i5p660">{thread.updatedAt}</span></div><p className="mt-1 line-clamp-2 text-[11px] leading-relaxed text-ink/55" data-id="element-85" data-mp="1d03qkkkqd6uj">{thread.preview}</p>{thread.waitingForReply && <span className="mt-2 inline-flex rounded-full bg-[#C9A04E]/15 px-1.5 py-0.5 text-[9px] font-semibold text-[#8B6A20]" data-id="element-86" data-mp="6k1gg1je7m20">Waiting for reply</span>}</div>{thread.unread && <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[#BB5E40]" data-id="element-87" data-mp="2d80i40iovesx" />}</div></button>)}{inboxThreads.length === 0 && <p className="px-3 py-8 text-center text-xs text-ink/50" data-id="element-88" data-mp="2i6lh5wm7fsfs">No conversations in this view.</p>}</div>
        </aside>

        <main className="flex min-w-0 flex-col" data-id="element-89" data-mp="2m7ngsek1a6ia"><header className="flex flex-wrap items-start justify-between gap-3 border-b border-ink/10 px-5 py-4 sm:px-7" data-id="element-90" data-mp="2m6lypq4lojr2"><div className="min-w-0" data-id="element-91" data-mp="2r19jpf0eaw0w"><div className="flex flex-wrap items-center gap-2" data-id="element-92" data-mp="2vz1d6dgouq77"><h1 className="text-lg font-semibold tracking-tight" data-id="element-93" data-mp="2vy9uu4c3qoyv">{activeThread.title}</h1><CategoryPill category={activeThread.category} data-id="element-94" data-mp="30wbasge8e864" /></div><p className="mt-1 text-xs text-ink/50" data-id="element-95" data-mp="35wpjm0nnpm57">{selectedBrand.name} · Private conversation with ManyCo</p></div>{activeThread.waitingForReply ? <span className="inline-flex items-center gap-1.5 rounded-full bg-[#C9A04E]/15 px-2.5 py-1 text-[11px] font-semibold text-[#8B6A20]" data-id="element-96" data-mp="39x9jhbzm73v1"><BellIcon className="h-3 w-3" data-id="element-97" data-mp="1bkhiu6onmb1i" />Needs reply</span> : <span className="inline-flex items-center gap-1.5 text-xs text-ink/50" data-id="element-98" data-mp="1fjhzbijnt1lc"><UserRoundIcon className="h-3.5 w-3.5" data-id="element-99" data-mp="2iv5u905vw5eg" />Account team</span>}</header>
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-6 sm:px-8" data-id="element-100" data-mp="2ix9wzes1qyd5">{activeThread.messages.map(message => <article key={message.id} className={`flex gap-3 ${message.author === 'ManyCo' ? 'justify-end' : ''}`} data-id="element-101" data-mp="2dyfcm8efl1qx">{message.author === 'Brand' && <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ink/70 text-[10px] font-bold text-cream" data-id="element-102" data-mp="2nu0msttnl8br">{selectedBrand.name.slice(0, 2).toUpperCase()}</span>}<div className={`max-w-[88%] ${message.author === 'ManyCo' ? 'rounded-2xl rounded-tr-sm bg-cream px-4 py-3' : ''}`} data-id="element-103" data-mp="2wv890diwaiph"><div className="mb-1 flex items-center gap-2 text-xs text-ink/45" data-id="element-104" data-mp="3bkcr7upowmxl"><span className="font-semibold text-ink/75" data-id="element-105" data-mp="3blnw5xuvg0qa">{message.authorName}</span><span data-id="element-106" data-mp="36l9z1aut6b71">{message.timestamp}</span></div><p className="text-sm leading-relaxed text-ink/80" data-id="element-107" data-mp="3kkj176m6j4s0">{message.text}</p></div></article>)}</div>
          <form onSubmit={sendReply} className="border-t border-ink/10 p-4 sm:px-7" data-id="element-108" data-mp="3flg9env7mv1z"><div className="rounded-2xl border border-ink/15 bg-cream/55 p-2 focus-within:border-[#BB5E40]/40" data-id="element-109" data-mp="3fn9cjt6m6x58"><label htmlFor="brand-message" className="sr-only" data-id="element-110" data-mp="32nbvms10ivh">Reply to brand</label><textarea id="brand-message" value={reply} onChange={event => setReply(event.target.value)} placeholder={`Reply to ${selectedBrand.name}…`} rows={2} className="block w-full resize-none bg-transparent px-2 py-1 text-sm outline-none placeholder:text-ink/40" data-id="element-111" data-mp="7zm1pt4nq3nv" /><div className="flex items-center justify-between gap-3 px-1 pt-2" data-id="element-112" data-mp="3gh0crxlayy2s"><button type="button" className="rounded p-1.5 text-ink/45 hover:bg-paper" aria-label="Attach a file" data-id="element-113" data-mp="3gfff3z3oyz0p"><PaperclipIcon className="h-4 w-4" data-id="element-114" data-mp="3l9jqcujzpt7l" /></button><button type="submit" disabled={!reply.trim()} className="inline-flex items-center gap-2 rounded-lg bg-ink px-3 py-2 text-xs font-semibold text-cream disabled:opacity-35" data-id="element-115" data-mp="36j2nr7vmcx94"><SendHorizontalIcon className="h-3.5 w-3.5" data-id="element-116" data-mp="3bfaxu6uym09f" />Send reply</button></div></div></form>
        </main>
      </div>
    </section>

    {newMessageOpen && <div className="fixed inset-0 z-50 flex items-end bg-ink/30 p-0 backdrop-blur-[1px] sm:items-center sm:justify-center sm:p-6" role="dialog" aria-modal="true" aria-labelledby="new-message-heading" data-id="element-117" data-mp="32fnwtm656wgw"><button type="button" className="absolute inset-0 cursor-default" onClick={() => setNewMessageOpen(false)} aria-label="Close start message" data-id="element-118" data-mp="3d0xgz4yyd90k" /><section className="relative z-10 w-full max-w-xl rounded-t-3xl bg-paper p-5 shadow-2xl sm:rounded-3xl sm:p-7" data-id="element-119" data-mp="382vusb68ctkv"><div className="flex items-start justify-between gap-4" data-id="element-120" data-mp="341k7icatwkfu"><div data-id="element-121" data-mp="2z0wejl31htiz"><p className="text-xs font-semibold uppercase tracking-wider text-[#BB5E40]" data-id="element-122" data-mp="2z1pdw0zo5gr3">New conversation</p><h2 id="new-message-heading" className="mt-2 text-xl font-semibold" data-id="element-123" data-mp="2u8mjjqn2i39u">Message for {selectedBrand.name}</h2></div><button type="button" onClick={() => setNewMessageOpen(false)} className="rounded-lg p-2 text-ink/45 hover:bg-cream" aria-label="Close" data-id="element-124" data-mp="2pautz39wat8t"><XIcon className="h-4 w-4" data-id="element-125" data-mp="2g9n3v8hj8ypt" /></button></div><form onSubmit={startConversation} className="mt-6 space-y-5" data-id="element-126" data-mp="2g8lk8fmgb3wh"><fieldset data-id="element-127" data-mp="2baschi5i21u3"><legend className="text-xs font-semibold text-ink/65" data-id="element-128" data-mp="2ba0fq4061y28">Send to</legend><div className="mt-2 grid gap-2 sm:grid-cols-3" data-id="element-129" data-mp="2bcwe1tfxeor7">{contacts.map(contact => {
                const isCoverage = contact.id === selectedBrand.coverageRep;
                return <label key={contact.id} className={`relative cursor-pointer rounded-xl border p-3 transition-colors ${recipientId === contact.id ? 'border-[#BB5E40] bg-[#BB5E40]/5' : 'border-ink/10 hover:border-[#BB5E40]/30'} ${!contact.available ? 'opacity-55' : ''}`} data-id="element-130" data-mp="vqt2rz6e0ka3"><input type="radio" name="recipient" value={contact.id} checked={recipientId === contact.id} onChange={() => contact.available && setRecipientId(contact.id)} disabled={!contact.available} className="sr-only" data-id="element-131" data-mp="14q6buqqln6ua" /><span className={`flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-bold text-cream ${contact.tone}`} data-id="element-132" data-mp="c190l7kt2ab1">{contact.initials}</span><span className="mt-2 block text-xs font-semibold" data-id="element-133" data-mp="lug931aue6fc">{contact.name}</span><span className="mt-0.5 block text-[10px] leading-snug text-ink/50" data-id="element-134" data-mp="3kjonhiqajv8q">{contact.available ? contact.role : 'Away · unavailable'}</span>{isCoverage && <span className="mt-2 block text-[9px] font-semibold text-[#94452E]" data-id="element-135" data-mp="3pgx5njbfvz9z">Default contact</span>}</label>;
              })}</div>{selectedBrand.coverageNote && <p className="mt-2 text-xs text-[#94452E]" data-id="element-136" data-mp="30yn6t1qgmhg6">{selectedBrand.coverageNote}</p>}</fieldset><div data-id="element-137" data-mp="310hw0s8rkvq2"><label htmlFor="new-subject" className="mb-1.5 block text-xs font-semibold text-ink/65" data-id="element-138" data-mp="310hrqg1qe9k9">Subject</label><input id="new-subject" value={subject} onChange={event => setSubject(event.target.value)} placeholder="What would you like to discuss?" className="w-full rounded-xl border border-ink/15 bg-cream/45 px-3 py-2.5 text-sm outline-none focus:border-[#BB5E40]/40" data-id="element-139" data-mp="3awt7xh4fzjjz" /></div><div data-id="element-140" data-mp="2i8f9x1dcsn8f"><label htmlFor="new-message" className="mb-1.5 block text-xs font-semibold text-ink/65" data-id="element-141" data-mp="2i8f8qy1mah6u">Message</label><textarea id="new-message" value={newMessage} onChange={event => setNewMessage(event.target.value)} placeholder="Write your message…" rows={4} className="w-full resize-none rounded-xl border border-ink/15 bg-cream/45 px-3 py-2.5 text-sm outline-none focus:border-[#BB5E40]/40" data-id="element-142" data-mp="2n5xhoofmwweu" /></div><div className="flex justify-end gap-2" data-id="element-143" data-mp="1zg3aame3401z"><button type="button" onClick={() => setNewMessageOpen(false)} className="rounded-lg px-3 py-2 text-xs font-semibold text-ink/60 hover:bg-cream" data-id="element-144" data-mp="1ry50shvwkoyr">Cancel</button><button type="submit" disabled={!subject.trim() || !newMessage.trim()} className="inline-flex items-center gap-2 rounded-lg bg-[#BB5E40] px-3 py-2 text-xs font-semibold text-cream hover:bg-[#94452E] disabled:opacity-35" data-id="element-145" data-mp="1mzleuhhb7d6e"><SendHorizontalIcon className="h-3.5 w-3.5" data-id="element-146" data-mp="1d2yv5w0pys5y" />Start message</button></div></form></section></div>}
  </div>;
}
function Toggle({
  checked,
  onChange,
  label
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return <button type="button" role="switch" aria-checked={checked} onClick={onChange} aria-label={label} className={`flex h-5 w-9 shrink-0 items-center overflow-hidden rounded-full p-0.5 transition-colors ${checked ? 'bg-[#BB5E40]' : 'bg-ink/20'}`} data-id="element-147" data-mp="z97wxjsfuupj"><span className={`h-4 w-4 rounded-full bg-paper shadow-sm transition-[margin] ${checked ? 'ml-auto' : 'ml-0'}`} data-id="element-148" data-mp="pa09u4vwjlp8" /></button>;
}
function CategoryPill({
  category
}: {
  category: MessageCategory;
}) {
  const className = category === 'Action needed' ? 'bg-[#C9A04E]/15 text-[#8B6A20]' : category === 'Update' ? 'bg-forest/10 text-forest' : 'bg-[#BB5E40]/10 text-[#94452E]';
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${className}`} data-id="element-149" data-mp="1oohsl2jz7rs">{category}</span>;
}